import React, { useState } from 'react';
import { Link as GatsbyLink, navigate } from 'gatsby';
import PropTypes from 'prop-types';
import { isNull, replace } from 'lodash';
import { Global, css } from '@emotion/react';

const BasicLink = ({ children, ...props }) => <a {...props}>{children}</a>;

const GatsbyJumpLink = ({ onClick, href, tagRef, ...rest }) => {
    const [smoothScroll, setSmoothScroll] = useState(false);

    return (
        <>
            {smoothScroll ? (
                <Global
                    styles={css`
                        html {
                            scroll-behavior: smooth;
                        }
                    `}
                />
            ) : null}
            <BasicLink
                {...rest}
                href={href}
                ref={tagRef}
                onClick={(event) => {
                    event.preventDefault();
                    setSmoothScroll(true);
                    navigate(href, {
                        state: {
                            disableScrollUpdate: true,
                            isJumpLink: true
                        }
                    });
                    if (onClick) {
                        onClick(event);
                    }
                }}
            />
        </>
    );
};

const isRelativeLink = (to) => {
    return to.charAt(0) === '#' || new RegExp(`^(/(?!/))`).test(to);
};

const isHashLink = (to) => {
    return /(#[a-zA-Z0-9_=-]*)/.test(to);
};

const shouldOpenInNewTab = ({ to, external, newTabOverride }) => {
    const url = replace(to, process.env.GATSBY_SITE_URL, '');
    switch (newTabOverride) {
        case 'newTab':
            return true;
        case 'sameTab':
            return false;
        default:
            return external || !isRelativeLink(url);
    }
};

const shouldUseGatsbyJumpLink = (props) => {
    const { to } = props;

    return !shouldOpenInNewTab(props) && isHashLink(to);
};

const shouldUseGatsbyLink = (props) => {
    const { to } = props;

    if (shouldOpenInNewTab(props)) {
        return false;
    }

    return isRelativeLink(to) && !isHashLink(to);
};

const formatProps = (props) => {
    const {
        ariaLabel,
        ariaExpanded,
        className,
        navIndex,
        tagLabel,
        tagRef,
        to,
        onClick,
        onKeyDown,
        state,
        isSubNav,
        activeStyle,
        partiallyActive,
        activeClassName
    } = props;

    const genericProps = {
        className,
        onKeyDown,
        onClick
    };

    if (shouldUseGatsbyJumpLink(props)) {
        genericProps.tagRef = tagRef;
    } else {
        if (tagRef && isSubNav) {
            genericProps.ref = (el) => {
                tagRef.current[navIndex] = el;
            };
        } else {
            genericProps.ref = tagRef;
        }
    }

    if (ariaLabel) {
        genericProps['aria-label'] = ariaLabel;
    }

    if (!isNull(ariaExpanded)) {
        genericProps['aria-expanded'] = ariaExpanded;
    }

    if (!isNull(tagLabel)) {
        genericProps['data-label'] = tagLabel;
    }

    if (shouldUseGatsbyLink(props)) {
        genericProps.activeStyle = activeStyle;
        genericProps.partiallyActive = partiallyActive;
        genericProps.activeClassName = activeClassName;
        return { to: to, state, ...genericProps };
    } else {
        const newTabAttributes = {};

        if (shouldOpenInNewTab(props)) {
            newTabAttributes.target = '_blank';
            newTabAttributes.rel = 'noopener noreferrer';
        }
        return {
            href: to,
            ...newTabAttributes,
            ...genericProps
        };
    }
};

const BaseLink = ({
    children,
    translationProps,
    onClick = () => {},
    navIndex = -1,
    ...props
}) => {
    const formattedProps = formatProps(props);

    let Link;

    if (shouldUseGatsbyLink(props)) {
        Link = GatsbyLink;
    } else if (shouldUseGatsbyJumpLink(props)) {
        Link = GatsbyJumpLink;
    } else {
        Link = BasicLink;
    }

    return <Link {...formattedProps}>{children}</Link>;
};

BaseLink.propTypes = {
    ariaLabel: PropTypes.string,
    ariaExpanded: PropTypes.bool,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    external: PropTypes.bool,
    navIndex: PropTypes.number,
    tagLabel: PropTypes.string,
    tagRef: PropTypes.object,
    to: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    state: PropTypes.object,
    isSubNav: PropTypes.bool
};

export default BaseLink;
